import { cn } from "@gymflow/helpers";
import { Transition } from "@headlessui/react";

import { SearchIcon } from "../../../icons";
import { LoadingDots } from "../../../LoadingDots";
import { OptionItem } from "./components/OptionItem";
import styles from "./OptionsList.module.css";
import { OptionsListProps } from "./types";

export const OptionsList = <T,>({
  dropPosition = "bottom",
  options,
  withImageByDefault = false,
  value,
  handleSelectOption,
  show,
  notFound = "Nothing found",
  notFoundClassName,
  onScroll,
  loading,
  listRef,
  listButton,
}: OptionsListProps<T>) => {
  return (
    <Transition
      show={show}
      className={cn(
        "absolute z-20 w-full rounded-xl border border-gray-200 dark:border-gray-800 bg-gray-0 dark:bg-gray-800 shadow-md",
        {
          "bottom-full -mb-6": dropPosition === "top",
          "mt-1.5": dropPosition === "bottom",
        },
      )}
      enter="transition ease-out duration-100"
      enterFrom="transform opacity-0 scale-95"
      enterTo="transform opacity-100 scale-100"
      leave="transition ease-in duration-75"
      leaveFrom="transform opacity-100 scale-100"
      leaveTo="transform opacity-0 scale-95"
    >
      <div className="flex max-h-[19.25rem] flex-col">
        <div
          ref={listRef}
          onScroll={onScroll}
          className={cn(
            "flex flex-col flex-1 overflow-y-auto px-2 py-1 gap-0.5",
            styles["scrollbarCustom"],
          )}
        >
          {options.length > 0 ? (
            options.map((option) => (
              <OptionItem
                key={option.id}
                option={option}
                isSelected={
                  value instanceof Array
                    ? value.length > 0 &&
                      value.some((v) => String(v.id) === String(option.id))
                    : value?.id === option.id
                }
                onSelect={handleSelectOption}
                withImageByDefault={withImageByDefault}
              />
            ))
          ) : !loading ? (
            <div
              className={cn(
                "flex items-center justify-center gap-2 p-2 text-sm font-medium text-gray-500",
                notFoundClassName,
              )}
            >
              {notFound || (
                <>
                  <SearchIcon /> Nothing found
                </>
              )}
            </div>
          ) : null}
          {loading && <LoadingDots />}
        </div>
        {listButton && (
          <div
            className="bg-gray-0 flex cursor-pointer flex-row gap-2 rounded-xl rounded-t-none border-t border-gray-300 px-3.5 py-3 hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:hover:bg-gray-900"
            onClick={() => {
              listButton.onClick();
            }}
          >
            {listButton?.icon}
            <div className="text-sm font-medium text-gray-950">
              {listButton.title}
            </div>
          </div>
        )}
      </div>
    </Transition>
  );
};
